/**
 * Em que abas do portfólio o item aparece.
 *
 * ## Por que não é um campo de texto
 *
 * As abas são uma lista fechada, e uma área que não corresponda a nenhuma delas
 * não dá erro: o item simplesmente **não aparece em lugar nenhum**, sem
 * mensagem, sem log, sem nada na tela que denuncie. "Marketing " com espaço no
 * fim, ou "RH." com ponto, custaria uma tarde de procura.
 *
 * Escolher de uma lista torna isso impossível de digitar errado.
 *
 * ## A regra mora em `areasSelecao.js`, e isso é correção
 *
 * O que um clique faz — inclusive o coringa `Todos` limpar o resto — saiu daqui
 * em 26/10. Motivo no cabeçalho de lá: a decisão de quais vínculos vão para o
 * banco estava num componente React, onde a suíte (que roda em Node) não
 * alcança, e um defeito de perda silenciosa de dado passou por revisão, portão
 * e deploy sem nada ficar vermelho.
 *
 * Aqui ficou só o desenho.
 */
import { useEffect, useState } from 'react';
import { AREAS_INICIAIS, carregarAreas } from '../areas.js';
import { TODAS, abasVisiveis, estaDesligada, alternarAba } from '../areasSelecao.js';

/**
 * @param {object} p
 * @param {string[]} p.value áreas escolhidas
 * @param {(areas: string[]) => void} p.onChange
 */
export function AreaPicker({ value, onChange }) {
  // Mesma razão do catálogo: a lista vem do servidor, e a de partida evita
  // o formulário nascer sem opção nenhuma.
  const [ativas, setAtivas] = useState(AREAS_INICIAIS);
  useEffect(() => {
    let vivo = true;
    carregarAreas().then((a) => vivo && setAtivas(a));
    return () => {
      vivo = false;
    };
  }, []);

  const escolhidas = new Set(value);
  const todas = escolhidas.has(TODAS);

  // Inclui as abas desligadas que ESTE item carrega — ver `areasSelecao.js`.
  const abas = abasVisiveis(ativas, value);
  const desligadas = abas.filter((a) => estaDesligada(a, ativas));

  return (
    <>
      <div className="area-picker" role="group" aria-label="Abas em que o item aparece">
        {[TODAS, ...abas].map((area) => {
          const acesa = area === TODAS ? todas : escolhidas.has(area);
          const fora = estaDesligada(area, ativas);
          return (
            <button
              key={area}
              type="button"
              aria-pressed={acesa}
              className={`area-chip ${acesa ? 'is-active' : ''} ${
                area === TODAS ? 'is-wildcard' : ''
              } ${fora ? 'is-desligada' : ''}`}
              onClick={() => onChange(alternarAba(ativas, value, area))}
              // Com o coringa ligado, as demais não têm efeito. Ficam apagadas
              // em vez de desabilitadas: desabilitar esconde do teclado e do
              // leitor de tela a informação de que elas existem.
              title={
                fora
                  ? 'Esta aba está desligada no cadastro: o vínculo continua gravado, mas ela não aparece no portfólio'
                  : todas && area !== TODAS
                    ? '“Todos” já faz o item aparecer em todas as abas'
                    : undefined
              }
            >
              {area}
              {fora && <span className="area-chip-nota"> · desligada</span>}
            </button>
          );
        })}
      </div>

      {/* Só aparece quando há o que explicar. A ficha de uma aba desligada é
          estranha o bastante para merecer uma frase, e sem ela a pessoa
          tenderia a tirá-la achando que é sobra. */}
      {desligadas.length > 0 && (
        <p className="field-hint">
          {desligadas.length === 1
            ? 'Uma das abas deste item está desligada no cadastro'
            : `${desligadas.length} abas deste item estão desligadas no cadastro`}
          : o vínculo continua gravado e volta a valer se a aba for religada. Tire a ficha só se
          quiser desfazer o vínculo de verdade.
        </p>
      )}
    </>
  );
}

export default AreaPicker;
